import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Plus,
  RotateCcw,
} from 'lucide-react'
import { memo, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'

import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Skeleton } from '@/components/ui/skeleton'
import useTranslator from '@/hooks/translation'
import { cn } from '@/lib/utils'

import { SidebarSubMenuItem } from '../menu-item/sub-menu-item'
import { SidebarItem, SidebarThemes } from '../types'

interface SidebarExpandableItemProps {
  item: SidebarItem
  theme?: SidebarThemes
}

export const SidebarExpandableItem = memo<SidebarExpandableItemProps>(
  ({ item, theme = 'light' }: SidebarExpandableItemProps) => {
    const {
      icon,
      title,
      onRetrySubItems,
      onAddSubItem,
      subItems,
      subItemsState = 'default',
    } = item

    const { getTranslation } = useTranslator()
    const { pathname } = useLocation()

    const hasActiveSubItem = useMemo(() => {
      return subItems?.some((subItem) => {
        return subItem.path.includes(pathname)
      })
    }, [subItems, pathname])

    const [isSubMenuOpen, setIsSubMenuOpen] = useState(hasActiveSubItem)

    useEffect(() => {
      if (hasActiveSubItem) {
        setIsSubMenuOpen(true)
      }
    }, [hasActiveSubItem])

    const handleMainItemClick = () => {
      if (subItems && subItems.length > 0) {
        setIsSubMenuOpen(!isSubMenuOpen)
      }
    }

    const onAddSubItemClick = onAddSubItem
      ? (e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault()
          onAddSubItem?.()
        }
      : undefined

    return (
      <li>
        <Collapsible open={isSubMenuOpen} onOpenChange={setIsSubMenuOpen}>
          <CollapsibleTrigger asChild>
            <div className="mx-4 flex items-center">
              <Button
                variant={theme === 'dark' ? 'botEnvironment' : 'ghost'}
                size="sm"
                onClick={handleMainItemClick}
                className={cn(
                  'w-full justify-between rounded-2xl',
                  hasActiveSubItem && '[&_svg]:[stroke-width:1.75]',
                )}
              >
                <span
                  className={cn(
                    theme === 'dark' ? 'text-white' : 'text-gray-500',
                  )}
                >
                  {icon}
                </span>

                <span
                  key="sidebar-title"
                  className="ml-2 flex-1 overflow-hidden text-left"
                >
                  {title}
                </span>

                <span>
                  {isSubMenuOpen ? <ChevronDown /> : <ChevronRight />}
                </span>
              </Button>
            </div>
          </CollapsibleTrigger>

          {subItemsState === 'loading' && (
            <CollapsibleContent className="mt-1">
              <Skeleton className="ml-11 mr-4 h-9 rounded-2xl" />
            </CollapsibleContent>
          )}

          {subItemsState === 'error' && (
            <CollapsibleContent className="mt-1 flex justify-center">
              {onRetrySubItems ? (
                <Button
                  variant="outline"
                  className="mb-5 mt-4"
                  onClick={onRetrySubItems}
                >
                  {getTranslation('components.sidebar.subItems.reload')}
                  <RotateCcw />
                </Button>
              ) : (
                <div className="mb-5 mt-4 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  <p className="text-sm text-destructive">
                    {getTranslation('components.sidebar.subItems.error')}
                  </p>
                </div>
              )}
            </CollapsibleContent>
          )}

          {subItemsState === 'default' && (
            <CollapsibleContent className="mt-1">
              <div className="ml-5">
                <ul className="mx-4 flex flex-col gap-1 border-l pl-2">
                  {subItems?.map((subItem) => (
                    <li key={subItem.index} className="flex">
                      <SidebarSubMenuItem
                        subItem={subItem}
                        variant="expandable"
                        theme={theme}
                      />
                    </li>
                  ))}

                  {onAddSubItemClick && (
                    <li className="flex">
                      <Button
                        variant={theme === 'dark' ? 'botEnvironment' : 'ghost'}
                        size="sm"
                        className={cn('flex-1 justify-start rounded-2xl')}
                        onClick={onAddSubItemClick}
                      >
                        <Plus />{' '}
                        {getTranslation('components.sidebar.subItems.add')}
                      </Button>
                    </li>
                  )}
                </ul>
              </div>
            </CollapsibleContent>
          )}
        </Collapsible>
      </li>
    )
  },
)

SidebarExpandableItem.displayName = 'SidebarExpandableItem'
